<template>
    <div class="ai-prompt-collect">
        <!-- 頂部導航 -->
        <div class="top-nav">
            <div class="nav-left">
                <h1 class="title">{{ $t('aiprompt.title') }}</h1>
            </div>
            <div class="nav-right">
                <!-- 未登入狀態 -->
                <div v-if="!isLogin" class="login-section">
                    <button class="login-btn" @click="loginTMID">
                        {{ $t('aiprompt.login') }}
                    </button>
                </div>
                
                <!-- 已登入狀態 -->
                <div v-else class="user-section">
                    <div class="user-info" @click="toggleUserMenu">
                        <img v-if="profile.picture" :src="profile.picture" class="avatar" alt="avatar">
                        <div v-else class="avatar-placeholder">{{ profile.name?.charAt(0) }}</div>
                        <span class="user-name">{{ profile.name }}</span>
                    </div>
                    
                    <!-- 用戶選單 -->
                    <div v-show="showUserMenu" class="user-menu">
                        <div class="menu-item">{{ profile.name }}</div>
                        <div class="menu-divider"></div>
                        
                        <!-- 管理員入口 (只有特定用戶才能看到) -->
                        <div v-if="isAdminUser" class="menu-item menu-action admin-entry" @click="goToAdminLogin">
                            {{ $t('aiprompt.adminEntry') }}
                        </div>
                        <div v-if="isAdminUser" class="menu-divider"></div>
                        
                        <div class="menu-item menu-action" @click="logout">
                            {{ $t('aiprompt.logout') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- 主要內容區域 -->
        <div class="main-content">
            <div class="content-layout">
                <!-- 中間內容區域 -->
                <div class="center-panel full-width">
                    <!-- 餐盤不公開警告 -->
                    <div v-if="!isPublicCollection" class="private-dish-warning">
                        <div class="warning-icon">
                            <svg-icon icon-class="lock" />
                        </div>
                        <h3>{{ $t('aiprompt.messages.dishNotPublic') }}</h3>
                    </div>
                    
                    <!-- 正常內容區域 -->
                    <div v-else class="content-area">
                    <!-- 餐盤標題與收藏功能 -->
                    <div v-if="collectionInfo" class="collection-header">
                        <h2 class="collection-name">{{ collectionInfo.CollectionName }}</h2>
                        <button
                            v-if="isLogin"
                            class="favorite-btn"
                            :class="{ favorited: isFavorited }"
                            @click="toggleFavorite"
                            :disabled="isFavoriteLoading"
                        >
                            <svg-icon icon-class="star-fill" />
                        </button>
                        <!-- 自動收藏成功提示 -->
                        <transition name="fade">
                            <span v-if="showAutoFavoriteMessage" class="auto-favorite-message">
                                {{ $t('aiprompt.messages.autoFavorited') }}
                            </span>
                        </transition>
                    </div>

                    <!-- 載入狀態 -->
                    <div v-if="isLoading" class="loading-section">
                        <div class="loading-spinner"></div>
                        <span>{{ $t('aiprompt.loadingPrompts') }}</span>
                    </div>
                    
                    <!-- 指令列表 -->
                    <div v-else-if="prompts.length > 0" class="prompts-list">
                        <div class="prompts-header">
                            <h3>{{ $t('aiprompt.promptList') }}</h3>
                        </div>
                        
                        <!-- 分類篩選按鈕 -->
                        <div class="filter-section">
                            <div class="filter-buttons">
                                <button 
                                    v-for="filter in availableFilters" 
                                    :key="filter"
                                    class="filter-btn"
                                    :class="{ active: selectedFilter === filter }"
                                    @click="applyFilter(filter)"
                                >
                                    {{ filter === 'showAll' ? $t('aiprompt.showAll') : getCategoryLabel(filter) }}
                                    <span v-if="filter !== 'showAll'" class="filter-count">
                                        {{ getFilterCount(filter) }}
                                    </span>
                                </button>
                            </div>
                        </div>
                        
                        <div class="prompt-grid">
                            <div 
                                v-for="prompt in filteredPrompts" 
                                :key="prompt.id"
                                :class="['prompt-card', { selected: selectedPromptForPreview?.id === prompt.id }]"
                                @click="showCustomPromptPreview(prompt)"
                            >
                                <div class="prompt-card-header">
                                    <h4 class="prompt-title">{{ prompt.purpose }}</h4>
                                </div>
                                
                                <div class="prompt-meta">
                                    <span class="prompt-category">{{ getCategoryLabel(prompt.category) }}</span>
                                    <!-- <span v-if="prompt.creator?.name" class="prompt-creator">by {{ prompt.creator.name }}</span> -->
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- 無資料狀態 -->
                    <div v-else class="empty-section">
                        <div class="empty-icon">
                            <svg-icon icon-class="empty-data" />
                        </div>
                        <p>{{ $t('aiprompt.noPromptsFound') }}</p>
                    </div>
                    </div>
                </div>
                
                <!-- 右側預覽面板（大螢幕時顯示） -->
                <div v-if="selectedPromptForPreview && !shouldShowPreviewAsModal" class="right-preview-panel show" style="width: 380px; min-width: 380px;">
                    <div class="preview-content">
                        <!-- 預覽標題欄 -->
                        <div class="preview-header">
                            <h3 class="preview-title">{{ selectedPromptForPreview.name || selectedPromptForPreview.purpose || $t('aiprompt.promptDetails') }}</h3>
                            <button class="close-preview-btn" @click="closePreview">
                                <svg-icon icon-class="close" />
                            </button>
                        </div>
                        
                        <!-- 預覽內容 -->
                        <div class="preview-body">
                            <!-- 自訂餐盤指令預覽 -->
                            <div class="custom-prompt-preview">
                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.promptCategory') }}</label>
                                    <div class="field-value">{{ getCategoryLabel(selectedPromptForPreview.category) }}</div>
                                </div>
                                
                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.promptPurpose') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.purpose }}</div>
                                </div>
                                
                                <div v-if="selectedPromptForPreview.usage" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.usage') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.usage }}</div>
                                </div>
                                
                                <div v-if="selectedPromptForPreview.sourceData" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.sourceData') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.sourceData }}</div>
                                </div>
                                
                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.conditionalPrompt') }}</label>
                                    <div class="field-value prompt-content">{{ selectedPromptForPreview.conditionalPrompt }}</div>
                                </div>
                                
                                <div v-if="selectedPromptForPreview.expectedOutput" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.expectedOutput') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.expectedOutput }}</div>
                                </div>
                                
                                <div v-if="selectedPromptForPreview.followUpActions" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.followUpActions') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.followUpActions }}</div>
                                </div>
                                
                                <div v-if="selectedPromptForPreview.personalNotes" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.personalNotes') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.personalNotes }}</div>
                                </div>
                                
                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.creator') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.creator?.name || $t('aiprompt.unknown') }}</div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- 預覽操作欄 -->
                        <div class="preview-actions">
                            <button class="action-btn copy-btn" @click="copyPromptContent">
                                <svg-icon icon-class="copy" />
                                {{ $t('aiprompt.sendToHT') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- 全屏模態預覽面板（中螢幕時顯示） -->
        <div v-if="shouldShowPreviewAsModal" class="modal-overlay preview-modal">
            <div class="modal-content preview-modal-content" @click.stop>
                <!-- 預覽標題欄 -->
                <div class="modal-header">
                    <h3>{{ selectedPromptForPreview.name || selectedPromptForPreview.purpose || $t('aiprompt.promptDetails') }}</h3>
                    <button class="close-btn" @click="closePreview">
                        <svg-icon icon-class="close" />
                    </button>
                </div>
                
                <!-- 預覽內容 -->
                <div class="modal-body preview-modal-body">
                    <!-- 自訂餐盤指令預覽 -->
                    <div class="custom-prompt-preview">
                        <div class="field-group">
                            <label class="field-label">{{ $t('aiprompt.promptCategory') }}</label>
                            <div class="field-value">{{ getCategoryLabel(selectedPromptForPreview.category) }}</div>
                        </div>
                        
                        <div class="field-group">
                            <label class="field-label">{{ $t('aiprompt.promptPurpose') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.purpose }}</div>
                        </div>
                        
                        <div v-if="selectedPromptForPreview.usage" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.usage') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.usage }}</div>
                        </div>
                        
                        <div v-if="selectedPromptForPreview.sourceData" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.sourceData') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.sourceData }}</div>
                        </div>
                        
                        <div class="field-group">
                            <label class="field-label">{{ $t('aiprompt.conditionalPrompt') }}</label>
                            <div class="field-value prompt-content">{{ selectedPromptForPreview.conditionalPrompt }}</div>
                        </div>
                        
                        <div v-if="selectedPromptForPreview.expectedOutput" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.expectedOutput') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.expectedOutput }}</div>
                        </div>
                        
                        <div v-if="selectedPromptForPreview.followUpActions" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.followUpActions') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.followUpActions }}</div>
                        </div>
                        
                        <div v-if="selectedPromptForPreview.personalNotes" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.personalNotes') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.personalNotes }}</div>
                        </div>
                        
                        <div class="field-group">
                            <label class="field-label">{{ $t('aiprompt.creator') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.creator?.name || $t('aiprompt.unknown') }}</div>
                        </div>
                    </div>
                </div>
                
                <!-- 預覽操作欄 -->
                <div class="modal-footer preview-actions">
                    <button class="btn-confirm copy-btn" @click="copyPromptContent">
                        <svg-icon icon-class="copy" />
                        {{ $t('aiprompt.sendToHT') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Message } from 'view-ui-plus'
import api from '@/api'
import aipromptAPI from '@/api/aipromptAPI'
import jwt_decode from 'jwt-decode'
import { useMainStore } from '@/stores/mainStore'
import profileConfig from '@/api/profile'
import SvgIcon from '@/components/SvgIcon.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const store = useMainStore()

// 狀態管理
const isLogin = ref(false)
const showUserMenu = ref(false)
const isAdminUser = ref(false)
// 移除 showSidebar，這個頁面不需要側邊欄
const profile = reactive({
    name: '',
    email: '',
    picture: '',
    teammodelId: ''
})

// 數據管理
const collectionId = ref('')
const prompts = ref([])
const filteredPrompts = ref([])
const isLoading = ref(false)
const selectedPromptForPreview = ref(null)
const windowWidth = ref(window.innerWidth)
const collectionInfo = ref(null)
const isPublicCollection = ref(true) // 預設為公開

// 收藏功能狀態
const isFavorited = ref(false)
const favoriteId = ref('')
const isFavoriteLoading = ref(false)
const showAutoFavoriteMessage = ref(false)

// 篩選器狀態
const selectedFilter = ref('showAll')
const availableFilters = ref([])

// 指令分類選項（使用計算屬性支援多語系）
const categoryOptions = computed(() => [
    { value: 'general', label: t('aiprompt.categories.general') },
    { value: 'score', label: t('aiprompt.categories.score') },
    { value: 'quiz', label: t('aiprompt.categories.quiz') },
    { value: 'pickup', label: t('aiprompt.categories.pickup') },
    { value: 'mindMap', label: t('aiprompt.categories.mindMap') },
    { value: 'custom', label: t('aiprompt.categories.custom') },
])

// 計算屬性
const shouldShowPreviewAsModal = computed(() => {
    return windowWidth.value < 1200 && selectedPromptForPreview.value !== null
})

// 初始化認證
const initializeAuth = async () => {
    try {
        const res = await api.main.getTokenUnlogin()
        const exp = jwt_decode(res.auth_token).exp
        localStorage.setItem("auth_token", res.auth_token)
        localStorage.setItem("auth_token_expiry", exp)
    } catch (error) {
        console.error("Error fetching auth token:", error)
    }
}

// 檢查自動登入
const checkAutoLogin = async () => {
    const savedProfile = localStorage.getItem("profile")
    const savedToken = localStorage.getItem("access_token")
    const idToken = localStorage.getItem("id_token")
    
    if (savedProfile && savedToken) {
        try {
            const profileData = JSON.parse(savedProfile)
            isLogin.value = true
            profile.name = profileData.name
            profile.email = profileData.email || ''
            profile.picture = profileData.picture
            store.setIsLogIn(true)
            
            // 如果有 id_token，解析並驗證用戶 ID
            if (idToken) {
                try {
                    const decoded = jwt_decode(idToken)
                    // 使用 token 中的用戶資料更新 profile
                    if (decoded.sub) {
                        profile.teammodelId = decoded.sub
                    }
                    if (decoded.email) {
                        profile.email = decoded.email
                    }
                } catch (tokenError) {
                    console.error("Token decode error:", tokenError)
                }
            }
            
            checkAdminUser()
        } catch (error) {
            console.error("Profile parse error:", error)
            // 如果 profile 解析失敗，清除損壞的資料
            localStorage.removeItem("profile")
            localStorage.removeItem("access_token")
            isLogin.value = false
        }
    }
}

// 處理 OAuth 回調
const handleOAuthCallback = async () => {
    const url = new URL(window.location.href)
    const code = url.searchParams.get("code")
    
    if (code) {
        try {
            const res = await api.main.getToken()
            localStorage.setItem("id_token", res.id_token)
            localStorage.setItem("access_token", res.access_token)
            localStorage.setItem("expires_in", res.expires_in)
            
            const decoded = jwt_decode(res.id_token)
            
            isLogin.value = true
            store.setIsLogIn(true)
            profile.name = decoded.name
            profile.email = decoded.email || ''
            profile.picture = decoded.picture
            
            localStorage.setItem("profile", JSON.stringify({
                name: profile.name,
                email: profile.email,
                picture: profile.picture
            }))
            
            checkAdminUser()
            
            // 清理 URL 中的 OAuth 參數，但保留其他參數如 ?id=
            const newUrl = new URL(window.location.href)
            newUrl.searchParams.delete('code')
            newUrl.searchParams.delete('state')
            newUrl.searchParams.delete('clientId')
            window.history.replaceState({}, document.title, newUrl.toString())
            
            Message.success(t('aiprompt.messages.loginSuccess'))
        } catch (error) {
            console.error("OAuth callback error:", error)
            Message.error(t('aiprompt.messages.loginFailed'))
        }
    }
}

const checkAdminUser = () => {
    const idToken = localStorage.getItem("id_token")
    
    if (!idToken || !isLogin.value) {
        isAdminUser.value = false
        return
    }
    
    try {
        const decodedToken = jwt_decode(idToken)
        const userEmail = decodedToken.email
        
        const adminEmails = profileConfig.adminUsers || []
        isAdminUser.value = adminEmails.includes(userEmail)
    } catch (error) {
        console.error('檢查管理員身份錯誤:', error)
        isAdminUser.value = false
    }
}

// 登入方法
const loginTMID = () => {
    api.main.loginTmdID()
}

// 登出方法
const logout = async () => {
    showUserMenu.value = false
    if (isLogin.value) {
        try {
            await api.main.logout()
            Message.info(t('aiprompt.messages.logoutSuccess'))
            isLogin.value = false
            store.setIsLogIn(false)
            
            // 清除本地儲存
            localStorage.removeItem('access_token')
            localStorage.removeItem('id_token')
            localStorage.removeItem('expires_in')
            localStorage.removeItem('profile')
            localStorage.removeItem('auth_token')
            localStorage.removeItem('auth_token_expiry')
            localStorage.removeItem('adminMode')
            
            // 清除用戶資料
            profile.name = ''
            profile.email = ''
            profile.picture = ''
            profile.teammodelId = ''
            isAdminUser.value = false
            
        } catch (error) {
            console.error('登出錯誤:', error)
            Message.error(t('aiprompt.messages.logoutFailed'))
        }
    }
}

const toggleUserMenu = () => {
    showUserMenu.value = !showUserMenu.value
}

// toggleSidebar 已移除，不需要側邊欄功能

const goToAdminLogin = () => {
    router.push('/admin-login')
    showUserMenu.value = false
}

// 收藏功能方法
const checkFavoriteStatus = async () => {
    if (!isLogin.value || !collectionId.value) {
        return
    }

    try {
        const response = await aipromptAPI.checkIsFavorite({
            userId: profile.teammodelId,
            collectionId: collectionId.value
        })

        if (response && response.success) {
            isFavorited.value = response.isFavorited
            if (response.favorite) {
                favoriteId.value = response.favorite.id
            }
        }
    } catch (error) {
        console.error('檢查收藏狀態失敗:', error)
    }
}

const autoAddFavorite = async () => {
    if (!isLogin.value || !collectionId.value || !collectionInfo.value) {
        return
    }

    // 先檢查是否已收藏
    const alreadyFavorited = isFavorited.value

    if (alreadyFavorited) {
        console.log('已經收藏過此餐盤，跳過自動收藏')
        return
    }

    try {
        isFavoriteLoading.value = true

        const response = await aipromptAPI.addFavorite({
            userId: profile.teammodelId,
            collectionId: collectionId.value
        })

        if (response && response.success) {
            isFavorited.value = true
            if (response.favorite) {
                favoriteId.value = response.favorite.id
            }

            // 顯示自動收藏提示訊息（2秒後消失）
            showAutoFavoriteMessage.value = true
            setTimeout(() => {
                showAutoFavoriteMessage.value = false
            }, 2000)

            console.log('自動加入收藏成功')
        }
    } catch (error) {
        console.error('自動加入收藏失敗:', error)
    } finally {
        isFavoriteLoading.value = false
    }
}

const toggleFavorite = async () => {
    if (!isLogin.value) {
        Message.warning(t('aiprompt.messages.pleaseLoginFirst'))
        return
    }

    if (isFavoriteLoading.value) return

    try {
        isFavoriteLoading.value = true

        if (isFavorited.value) {
            // 取消收藏
            const response = await aipromptAPI.removeFavorite({
                favoriteId: favoriteId.value
            })

            if (response && response.success) {
                isFavorited.value = false
                favoriteId.value = ''
                Message.success(t('aiprompt.messages.unfavorited'))
            }
        } else {
            // 加入收藏
            const response = await aipromptAPI.addFavorite({
                userId: profile.teammodelId,
                collectionId: collectionId.value
            })

            if (response && response.success) {
                isFavorited.value = true
                if (response.favorite) {
                    favoriteId.value = response.favorite.id
                }
                Message.success(t('aiprompt.messages.favorited'))
            }
        }
    } catch (error) {
        console.error('收藏操作失敗:', error)
        Message.error(t('aiprompt.messages.favoriteFailed'))
    } finally {
        isFavoriteLoading.value = false
    }
}

// 數據操作方法
const getPromptsByCollection = async () => {
    if (!collectionId.value) {
        console.error('Collection ID is required')
        return
    }

    isLoading.value = true

    try {
        const response = await aipromptAPI.getPromptsByCollection({
            collectionId: collectionId.value
        })

        if (response && response.data) {
               // 檢查餐盤的 isPublic 狀態
               if (response.collection) {
                collectionInfo.value = response.collection
                isPublicCollection.value = response.collection.isPublic !== false

                // 如果餐盤不公開，顯示警告並返回
                if (!isPublicCollection.value) {
                    Message.warning(t('aiprompt.messages.dishNotPublic'))
                    return
                }
            }
            prompts.value = response.data
            // 初始化篩選器
            updateAvailableFilters()
            filteredPrompts.value = prompts.value
            selectedFilter.value = 'showAll'
            console.log('載入餐盤指令成功:', prompts.value)

            // 檢查收藏狀態
            await checkFavoriteStatus()

            // 如果已登入且餐盤公開，自動加入收藏
            if (isLogin.value && isPublicCollection.value) {
                await autoAddFavorite()
            }
        } else {
            prompts.value = []
            filteredPrompts.value = []
            availableFilters.value = ['showAll']
        }
    } catch (error) {
        console.error('載入餐盤指令失敗:', error)
        prompts.value = []
        filteredPrompts.value = []
        availableFilters.value = ['showAll']
    } finally {
        isLoading.value = false
    }
}

// 取得分類中文標籤
const getCategoryLabel = (categoryValue) => {
    const option = categoryOptions.value.find(opt => opt.value === categoryValue)
    return option ? option.label : categoryValue
}

// 篩選器邏輯
const applyFilter = (filterName) => {
    selectedFilter.value = filterName
    
    if (filterName === 'showAll') {
        filteredPrompts.value = prompts.value
    } else {
        filteredPrompts.value = prompts.value.filter(prompt => 
            prompt.category === filterName
        )
    }
    
    console.log(`篩選器已應用: ${filterName}`)
    console.log(`顯示 ${filteredPrompts.value.length} 個指令`)
}

// 獲取篩選器數量
const getFilterCount = (filterName) => {
    if (filterName === 'showAll') {
        return prompts.value.length
    }
    return prompts.value.filter(prompt => 
        prompt.category === filterName
    ).length
}

// 更新可用篩選器
const updateAvailableFilters = () => {
    const categories = [...new Set(prompts.value.map(prompt => prompt.category))]
    availableFilters.value = ['showAll', ...categories]
}

const showCustomPromptPreview = (prompt) => {
    selectedPromptForPreview.value = prompt
    console.log(`顯示自訂指令「${prompt.purpose}」的預覽`)
}

const closePreview = () => {
    selectedPromptForPreview.value = null
}

const copyPromptContent = async () => {
    if (!selectedPromptForPreview.value) return
    
    try {
        // 自訂餐盤內容 - 統一格式
        const jsonData = {
            type: 'custom',
            name: selectedPromptForPreview.value.purpose,
            category: selectedPromptForPreview.value.category,
            categoryLabel: getCategoryLabel(selectedPromptForPreview.value.category),
            usage: selectedPromptForPreview.value.usage || null,
            conditionalPrompt: selectedPromptForPreview.value.conditionalPrompt || selectedPromptForPreview.value.content,
            expectedOutput: selectedPromptForPreview.value.expectedOutput || null,
            followUpActions: selectedPromptForPreview.value.followUpActions || null,
            personalNotes: selectedPromptForPreview.value.personalNotes || null,
            sourceMaterial: selectedPromptForPreview.value.sourceData || null,
            creator: selectedPromptForPreview.value.creator || null,
            collectionId: collectionId.value || null,
            collectionName: null // 可以後續從 API 獲取餐盤名稱
        }
        
        // Debug: 在 console 印出格式化的 JSON
        console.log('=== 傳送給 HT 的指令資料 ===')
        console.log(jsonData)
        
        // 使用通訊協議發送到 HT (替代原本的 console 印出)
        if (isWebView.value) {
            sendAIPromptToHT(jsonData)
            // 顯示成功訊息
            Message.success(t('aiprompt.messages.sentToHT'))
        } else {
            // 非 WebView 環境，仍然只在 console 印出
            Message.info(t('aiprompt.messages.outputToConsole'))
        }
        
    } catch (error) {
        console.error('傳送指令資料失敗:', error)
        Message.error(t('aiprompt.messages.sendFailed'))
    }
}

// 視窗大小監聽
const handleResize = () => {
    windowWidth.value = window.innerWidth
}

// 點擊外部關閉選單
const handleClickOutside = (event) => {
    if (showUserMenu.value) {
        const userSection = event.target.closest('.user-section')
        if (!userSection) {
            showUserMenu.value = false
        }
    }
}

// HT WebView 通訊橋接器 (參考 molegame-play 的嚴謹作法)
const MessageBridge = {
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    // 當前環境
    currentEnv: 'webview',
    // 初始化
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    // 發送消息
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            window.postMessage(data, origin);
        }
    },
    // 添加消息監聽器
    addEventListener(callback) {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', event => {
                callback(event);
            });
        } else {
            window.addEventListener('message', event => {
                callback(event);
            });
        }
    }
};

// 初始化消息橋接器
const messageBridge = MessageBridge.init();

// WebView 環境檢測
const isWebView = computed(() => {
    return messageBridge.currentEnv === MessageBridge.ENV.WEBVIEW;
});

// 發送 AI Prompt 資料到 HT
const sendAIPromptToHT = (promptData) => {
    const messageData = {
        action: 'Send.AIPrompt',
        data: {...promptData }
    };
    
    console.log('發送 AI Prompt 到 HT:', messageData);
    messageBridge.postMessage(messageData);
};

// 生命週期鉤子
onMounted(async () => {
    // 獲取URL中的id參數
    collectionId.value = route.query.id || route.params.id
    console.log('Collection ID from URL:', collectionId.value)
    
    // 初始化認證
    await initializeAuth()
    
    // 檢查自動登入
    await checkAutoLogin()
    
    // 處理 OAuth 回調
    await handleOAuthCallback()
    
    // 如果有collection ID，就載入指令
    if (collectionId.value) {
        await getPromptsByCollection()
    } else {
        console.error('No collection ID found in URL')
    }
    
    // 事件監聽
    window.addEventListener('resize', handleResize)
    document.addEventListener('click', handleClickOutside)
    
    // 初始化 HT WebView 通訊監聽 (參考 MindMap.vue 的 initialMindMap)
    console.log('🔧 MessageBridge 環境檢測:', {
        currentEnv: messageBridge.currentEnv,
        hasWebView: !!window?.chrome?.webview,
        hasWindow: !!window
    })
})

onUnmounted(() => {
    window.removeEventListener('resize', handleResize)
    document.removeEventListener('click', handleClickOutside)
})
</script>
<style lang="less">
@import "../assets/AIPromptCollect.less";
</style>